<script setup lang="ts">
// Solid pie chart. Same data shape as donut without the inner radius —
// reaches authors via `sbi.viz.pie` directly so the briefing can
// document each intent separately.

import { computed } from "vue";

import { resolveChartHeight } from "./chart-heights";
import VChart from "vue-echarts";
import "../echarts.ts";

import type { PieChartSpec } from "../sbi/protocol";
import { pieOption } from "./pie-base";
import { useChartLayout } from "./use-chart-layout";

const props = withDefaults(
  defineProps<{
    spec: PieChartSpec;
    formats?: Record<string, string>;
  }>(),
  { formats: () => ({}) },
);

const emit = defineEmits<{
  (e: "click-target", id: string, datum: Record<string, unknown>): void;
}>();

const rows = computed<ReadonlyArray<Record<string, unknown>>>(() =>
  Array.isArray(props.spec.data) ? props.spec.data : [],
);

const height = computed(() =>
  resolveChartHeight("pie", props.spec.height),
);

// Chart width picks the legend layout (side vs bottom) in
// `pieOption`; ECharts' own `autoresize` only rescales the canvas.
// Measured on `<v-chart>` itself (it must stay the template root so
// InsightChart's scoped `.chart { display: block }` reaches it),
// quantised to 16px and seeded from the embed width so the first
// option already has the right legend.
const { chartRef, ctx } = useChartLayout();

const option = computed(() =>
  pieOption({
    rows: rows.value,
    category: props.spec.category,
    value: props.spec.value,
    format: props.formats[props.spec.value],
    radius: "70%",
    width: ctx.value.width,
    height: height.value,
    print: ctx.value.print,
  }),
);

function onClick(p: { dataIndex?: number; data?: unknown; percent?: number }) {
  if (!props.spec.id || typeof p?.dataIndex !== "number") return;
  const datum = (p.data ?? {}) as { value?: number; name?: string; folded?: boolean };
  // The folded "Other" slice isn't a category in the data — there is
  // nothing to filter on.
  if (datum.folded) return;
  emit("click-target", props.spec.id, {
    category: datum.name ?? "",
    value: datum.value ?? 0,
    percent: p.percent ?? 0,
  });
}
</script>

<template>
  <v-chart
    ref="chartRef"
    class="chart"
    :style="{ height: `${height}px` }"
    :option="option"
    :init-options="{ renderer: 'canvas' }"
    autoresize
    @click="onClick"
  />
</template>

<style scoped>
.chart {
  width: 100%;
}
</style>
